import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { formatLocalizedTime } from "../../_common/functions";

interface FormattedDateProps {
  date?: string | Date | moment.Moment | null | undefined;
  startDate?: string | Date | moment.Moment | null | undefined;
  endDate?: string | Date | moment.Moment | null | undefined;
  format?: "date" | "dateTime" | "dateTimeSeconds" | "dayDateTime" | "monthDayYear" | "monthDayYearWithNoTime" | "time";
  isUTC?: boolean;
}

// Date portions keep moment's locale-invariant numeric/abbreviated format exactly
// as before (this app never registers a moment locale, so weekday/month names
// would render in English regardless -- out of scope here, see chartDateFormat.ts
// for date-heavy displays that already went through that fix). Only the "hh:mm A"
// time suffix -- which literally can't render as anything but English "AM"/"PM"
// -- gets swapped for a real Intl-localized day-period string via formatLocalizedTime.
const FORMAT_CONFIG: Record<string, { datePart?: string; hasTime?: boolean; withSeconds?: boolean }> = {
  date: { datePart: "MM/DD/YYYY" },
  dateTime: { datePart: "MM/DD/YYYY,", hasTime: true },
  dateTimeSeconds: { datePart: "MM/DD/YYYY,", hasTime: true, withSeconds: true },
  dayDateTime: { datePart: "ddd, MM/DD/YYYY", hasTime: true },
  monthDayYear: { datePart: "MMM DD, YYYY,", hasTime: true },
  monthDayYearWithNoTime: { datePart: "MMM DD, YYYY" },
  time: { hasTime: true },
};

function FormattedDate({ date, startDate, endDate, format = "date", isUTC = true }: FormattedDateProps) {
  const { t, i18n } = useTranslation("common");

  const formatValue = (value: string | Date | moment.Moment): string => {
    const m = isUTC ? moment.utc(value).local() : moment(value);
    const config = FORMAT_CONFIG[format] ?? FORMAT_CONFIG.date;
    const datePart = config.datePart ? m.format(config.datePart) : "";
    if (!config.hasTime) return datePart;
    const timePart = formatLocalizedTime(
      m.toDate(),
      i18n.language,
      config.withSeconds ? { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true } : undefined
    );
    return datePart ? `${datePart} ${timePart}` : timePart;
  };

  // Handle date range
  if (startDate || endDate) {
    if (!startDate && !endDate) {
      return <span>-</span>;
    }

    if (startDate && endDate) {
      return <span>{formatValue(startDate)} - {formatValue(endDate)}</span>;
    } else if (startDate) {
      return <span>{t("dateRange.from", { date: formatValue(startDate) })}</span>;
    } else if (endDate) {
      return <span>{t("dateRange.until", { date: formatValue(endDate) })}</span>;
    }
  }

  // Handle single date
  if (!date) {
    return <span>-</span>;
  }

  return <span>{formatValue(date)}</span>;
}

export default FormattedDate;
